import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({
    providedIn: "root",
})
/**
 * This service wraps angular HttpClient.
 * All the http request will be use this wrapper.
 */
export class HttpService {
    /**
     * The server base url
     * @private
     * @readonly
     */
    private readonly _baseUrl: string = `${environment.api_base_url}/${environment.api_version}`;

    /**
     * The beta server base url
     * @private
     * @readonly
     */
    private readonly _betaBaseUrl: string = `${environment.api_base_url}/${environment.api_beta_version}`;

    /**
     * The base headers for each http request.
     * @private
     * @readonly
     */
    private readonly _headers: HttpHeaders = new HttpHeaders({
        "Content-Type": "application/json",
    });

    constructor(private _http: HttpClient) {}

    /**
     * @public Generic method to make a Get request.
     * @template TResponse an array or an object
     * @param {string} endpoint The endpoint to call
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{TResponse} with the data of the generic type received
     */
    public get<TResponse>(endpoint: string, headers?: HttpHeaders, params?: HttpParams, beta = false): Observable<TResponse> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.get<TResponse>(url, {
            headers: mergedHeaders,
            params: params,
            responseType: "json",
        });
    }

    /**
     * @public Generic method to make a Get request.
     * @param {string} endpoint The endpoint to call
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{String} with the data of the generic type received
     */
    public getStream(endpoint: string, headers?: HttpHeaders, params?: HttpParams, beta = false): Observable<Blob> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;

        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.get(url, {
            headers: mergedHeaders,
            params: params,
            responseType: "blob",
        });
    }

    /**
     * @public Generic method to make a Delete request.
     * @template TResponse an array or an object
     * @param {string} endpoint The endpoint to call
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{TResponse} with the data of the generic type received
     */
    public delete<TResponse>(endpoint: string, headers?: HttpHeaders, params?: HttpParams, beta = false): Observable<TResponse> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.delete<TResponse>(url, {
            headers: mergedHeaders,
            params: params,
            responseType: "json",
        });
    }

    /**
     * @public Generic method to make a Delete request.
     * @template TBody Type of the body param, an array or an object
     * @template TResponse Type of the response, an array or an object
     * @param {string} endpoint The endpoint to call
     * @param {TBody} body The request body
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{TResponse} with the data of the generic type received
     */
    public delete_with_body<TBody, TResponse>(
        endpoint: string,
        body?: TBody,
        headers?: HttpHeaders,
        params?: HttpParams,
        beta = false,
    ): Observable<TResponse> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.delete<TResponse>(url, {
            headers: mergedHeaders,
            params: params,
            responseType: "json",
            body: body,
        });
    }

    /**
     * @public Generic method to make a Put request.
     * @template TBody Type of the body param, an array or an object
     * @template TResponse Type of the response, an array or an object
     * @param {string} endpoint The endpoint to call
     * @param {TBody} body The request body
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{TResponse} with the data of the generic type received
     */
    public put<TResponse, TBody = void>(
        endpoint: string,
        body?: TBody,
        headers?: HttpHeaders,
        params?: HttpParams,
        beta = false,
    ): Observable<TResponse> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.put<TResponse>(url, body, {
            headers: mergedHeaders,
            params: params,
            responseType: "json",
        });
    }

    /**
     * @public Generic method to make a Post request.
     * @template TBody Type of the body param, an array or an object
     * @template TResponse Type of the response, an array or an object
     * @param {string} endpoint The endpoint to call
     * @param {TBody} body The request body
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{TResponse} with the data of the generic type received
     */
    public post<TBody, TResponse>(endpoint: string, body: TBody, headers?: HttpHeaders, params?: HttpParams, beta = false): Observable<TResponse> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.post<TResponse>(url, body, {
            headers: mergedHeaders,
            params: params,
            responseType: "json",
        });
    }

    /**
     * @public Generic method to make a Patch request.
     * @template TBody Type of the body param, an array or an object
     * @template TResponse Type of the response, an array or an object
     * @param {string} endpoint The endpoint to call
     * @param {TBody} body The request body
     * @param {HttpHeaders | undefined} headers Specific headers to add to the http request
     * @param {HttpParams | undefined} params The query params to add to the http request
     * @returns an Observable{TResponse} with the data of the generic type received
     * @memberof HttpService
     * @returns an Observable{TResponse} with the data of the generic type received
     **/
    public patch<TBody, TResponse>(endpoint: string, body: TBody, headers?: HttpHeaders, params?: HttpParams, beta = false): Observable<TResponse> {
        const url = `${beta ? this._betaBaseUrl : this._baseUrl}/${endpoint}`;
        const mergedHeaders = this._mergeHeaders(headers);

        return this._http.patch<TResponse>(url, body, {
            headers: mergedHeaders,
            params: params,
            responseType: "json",
        });
    }

    /**
     * Get query string to concatenate to api calls
     * @param queryStringValues - an array of query string values, if query string accepts one value, then array should have one item
     * @param queryStringName - query string name
     * @returns - query_name=query_value1,query_value2
     */
    public getQueryString(queryStringValues: string[], queryStringName: string): string {
        let queryString = "";

        if (queryStringValues.length > 0) {
            queryString = queryStringName;
            queryStringValues = queryStringValues.map((element) => encodeURIComponent(element));
            const queryStringValuesArrayString: string = queryStringValues.join(",");
            queryString = queryString + "=" + queryStringValuesArrayString;
        }

        return queryString;
    }

    /**
     * Creates a new HttpHeaders based on the baseHeaders and the received headers of the method
     * @param {HttpHeaders | null} headers The headers to merge
     * @returns the HttpHeaders to send to the request
     */
    private _mergeHeaders(headers?: HttpHeaders): HttpHeaders {
        if (!headers) {
            return this._headers;
        }

        let newHeaders: HttpHeaders = new HttpHeaders();

        for (const key in this._headers.keys()) {
            const headerValue = this._headers.get(key);
            if (headerValue !== null) {
                newHeaders = headers.append(key, headerValue);
            }
        }

        for (const key in headers.keys()) {
            const headerValue = headers.get(key);
            if (headerValue !== null) {
                newHeaders = headers.append(key, headerValue);
            }
        }
        return newHeaders;
    }
}
